"use client";

import { Globe, Lock, Mail, MessageCircle, Phone } from "lucide-react";
import { useHolidayQuoteBrand } from "../contexts/use-holiday-quote-brand";
import {
  quoteThemeColorStyles,
  quoteThemeCssVars,
} from "../quote-theme/quote-theme-css-vars";

/** Server-safe HTML to text (the shared helper needs `document`). */
function htmlToText(html: string) {
  return html
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Replaces the booking / deposit block on Free Quote Builder pages.
 * Travellers contact the agent directly; in the builder, the "Book now"
 * button shows what TA Desk adds (online booking, deposits).
 */
export function FreeContactAgentSection({ title }: { title?: string }) {
  const { freeMode, companyName } = useHolidayQuoteBrand();
  if (!freeMode) return null;

  const { agent, website, onLockedAction } = freeMode;
  const heading =
    htmlToText(title || "") || "Ready to book this holiday?";
  const whatsappNumber = agent.whatsapp?.replace(/[^\d]/g, "");
  const agentName = agent.name || companyName;

  const actions = [
    agent.email && {
      key: "email",
      href: `mailto:${agent.email}`,
      label: "Email",
      Icon: Mail,
    },
    agent.phone && {
      key: "phone",
      href: `tel:${agent.phone.replace(/\s+/g, "")}`,
      label: "Call",
      Icon: Phone,
    },
    whatsappNumber && {
      key: "whatsapp",
      href: `https://wa.me/${whatsappNumber}`,
      label: "WhatsApp",
      Icon: MessageCircle,
    },
    !agent.email && !agent.phone && !whatsappNumber && website && {
      key: "website",
      href: website,
      label: "Visit website",
      Icon: Globe,
    },
  ].filter(Boolean) as {
    key: string;
    href: string;
    label: string;
    Icon: typeof Mail;
  }[];

  return (
    <section
      className="border p-5 sm:p-8"
      style={{
        borderRadius: quoteThemeCssVars.radiusCard,
        boxShadow: quoteThemeCssVars.shadowCard,
        ...quoteThemeColorStyles.diagonalGradientSurface,
      }}
      data-testid="free-contact-agent"
    >
      <h3
        className="text-xl sm:text-2xl font-semibold"
        style={{
          fontFamily: quoteThemeCssVars.headingFont,
          color: quoteThemeCssVars.blockHeading,
        }}
      >
        {heading}
      </h3>
      <p className="mt-2 text-sm sm:text-base text-gray-700">
        Get in touch with {agentName} to confirm availability and book.
      </p>

      <div className="mt-5 flex flex-wrap gap-3">
        {onLockedAction && (
          <button
            type="button"
            onClick={() => onLockedAction("online_booking")}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold"
            style={{
              ...quoteThemeColorStyles.primaryButton,
              borderRadius: quoteThemeCssVars.radiusPill,
            }}
          >
            <Lock className="h-4 w-4" />
            Book now
          </button>
        )}
        {actions.map(({ key, href, label, Icon }) => (
          <a
            key={key}
            href={href}
            target={key === "email" || key === "phone" ? undefined : "_blank"}
            rel="noopener noreferrer nofollow"
            className="inline-flex items-center gap-2 border bg-white px-5 py-2.5 text-sm font-semibold"
            style={{
              borderRadius: quoteThemeCssVars.radiusPill,
              ...(onLockedAction ? {} : quoteThemeColorStyles.primaryButton),
              ...(onLockedAction ? quoteThemeColorStyles.primary : {}),
            }}
          >
            <Icon className="h-4 w-4" />
            {label}
          </a>
        ))}
        {actions.length === 0 && !onLockedAction && (
          <p className="text-sm text-gray-600">
            Reply to the message this link came with.
          </p>
        )}
      </div>
    </section>
  );
}
